import React from "react";
import { Link, LinkProps } from "react-router-dom";
import { useSmartNavigate } from "@/hooks/useSmartNavigate";
import { useOptionalStorefront } from "@/components/store/shared/StorefrontContext";
import { resolveInternalLink } from "@/lib/storefront/pageIntegrity";
import { resolveSafeLink } from "@/lib/storefront/resolveSafeLink";

interface SmartLinkProps extends Omit<LinkProps, "to"> {
    to: string;
}

function isNonRouterTarget(to: string): boolean {
    const t = to.trim();
    return (
        /^https?:\/\//i.test(t) ||
        t.startsWith("//") ||
        t.startsWith("#") ||
        t.startsWith("mailto:") ||
        t.startsWith("tel:")
    );
}

/**
 * Router link with store slug prefix + internal path validation via `resolveInternalLink`.
 */
export const SmartLink: React.FC<SmartLinkProps> = ({ to, children, ...props }) => {
    const { getSmartPath } = useSmartNavigate();
    const optional = useOptionalStorefront();
    const layoutForResolve = optional?.template?.pages
        ? { pages: optional.template.pages as Record<string, unknown> }
        : undefined;

    if (isNonRouterTarget(to)) {
        return (
            <a href={to} {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}>
                {children}
            </a>
        );
    }

    const safeTo = resolveInternalLink(resolveSafeLink(to), layoutForResolve);
    const smartTo = getSmartPath(safeTo);

    return (
        <Link to={smartTo} {...props}>
            {children}
        </Link>
    );
};
